import { useMemo, useState } from 'react';
import { useModalState } from 'hooks/useModalState';
import { Grid, Typography } from '@mui/material';
import { DraftCardCount, DraftCardMediaItem } from './components';
import PhotoViewer from 'components/photo-viewer';
import { adaptMediaForPhotoViewer } from './utils';
import { IDraftCardMedia } from './types';
import { TActivityFeedDraftItemUploads } from 'redux-store/api/activity-feed';
import { isVideo } from 'components/uploaded-images/helpers';
import { useVideoThumbnails } from 'hooks/useVideoThumbnails';
import { styles } from './styles';

export const DraftCardMedia = ({ media, hasContent }: IDraftCardMedia) => {
  const [isMediaViewerOpen, openMediaViewer, closeMediaViewer] = useModalState();
  const [activeMediaIndex, setActiveMediaIndex] = useState(0);

  const videoEntries = useMemo(
    () =>
      (media as Array<{ id: string; url: string; mimeType?: string; options?: { previewUrl?: string } }>)
        .filter((item) => isVideo(item.mimeType) && !item.options?.previewUrl)
        .map((item) => ({ id: item.id, url: item.url })),
    [media]
  );

  const videoThumbnails = useVideoThumbnails(videoEntries);

  const photoViewerMedia = useMemo(() => adaptMediaForPhotoViewer(media, videoThumbnails), [media, videoThumbnails]);

  const handleMediaViewerOpen = (index: number) => {
    setActiveMediaIndex(index);
    openMediaViewer();
  };

  const displayLimit = hasContent ? 1 : 5;
  const mediaToShow = media.slice(0, displayLimit);
  const extraCount = media.length - mediaToShow.length;

  return (
    <>
      {hasContent ? (
        <Grid sx={styles.singleItemContainer}>
          {media.length > 1 && <DraftCardCount count={media.length} />}
          <DraftCardMediaItem onMediaViewerOpen={() => handleMediaViewerOpen(0)} item={media[0]} videoThumbnails={videoThumbnails} />
        </Grid>
      ) : (
        <Grid sx={styles.multipleItemsContainer}>
          {mediaToShow.map((item: TActivityFeedDraftItemUploads[number], index: number) => (
            <DraftCardMediaItem
              onMediaViewerOpen={() => handleMediaViewerOpen(index)}
              key={item.id}
              item={item}
              videoThumbnails={videoThumbnails}
            />
          ))}
          {extraCount > 1 && <Typography sx={styles.extraCount}>+{extraCount}</Typography>}
        </Grid>
      )}
      <PhotoViewer
        isOpen={isMediaViewerOpen}
        activeIndex={activeMediaIndex}
        imagesList={photoViewerMedia}
        handleClose={closeMediaViewer}
      />
    </>
  );
};
